import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { CERTIFICATE } from "@/constants/testIds";
import { Award, ArrowLeft, Printer, ShieldCheck, Trophy, CheckCircle2 } from "lucide-react";

export default function Certificate() {
  const { t, i18n } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/certificates/verify/${code}`)
      .then((r) => setCert(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, [code]);

  // Fire the celebration confetti exactly once, the moment the certificate
  // renders for the first time in this session. `sessionStorage` guarantees
  // that reloading / re-printing doesn't re-trigger it.
  useEffect(() => {
    if (!cert) return;
    const key = `cert-celebrated-${cert.code}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    // Small delay so the paper is on screen when confetti fires.
    const timer = setTimeout(() => celebrate(), 350);
    return () => clearTimeout(timer);
  }, [cert]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-16 text-center">
        <div className="text-xs uppercase tracking-[0.28em] text-red-600 font-semibold mb-3">— 404</div>
        <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">
          {t("certificate.invalidTitle")}
        </h1>
        <p className="mt-3 text-slate-600">{t("certificate.invalidBody")}</p>
        <Button onClick={() => navigate("/")} className="mt-6 bg-blue-800 text-white">
          {t("certificate.backButton")}
        </Button>
      </div>
    );
  }

  if (!cert) return <div className="p-16 text-slate-500">{t("common.loading")}</div>;

  const issued = new Date(cert.issued_at);
  const issuedLabel = issued.toLocaleDateString(i18n.language, {
    year: "numeric", month: "long", day: "numeric",
  });
  const verifyUrl = `${window.location.origin}/certificates/${cert.code}`;
  const isMaster = Boolean(cert.master);
  const isMastery = !isMaster && cert.variant === "mastery";

  return (
    <div data-testid={CERTIFICATE.root} className="bg-slate-100 print:bg-white min-h-screen py-10 print:py-0">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 landscape; margin: 12mm; }
        }
      `}</style>
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="no-print flex flex-wrap justify-between gap-3 mb-6">
          <Button
            data-testid={CERTIFICATE.backButton}
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-slate-300"
          >
            <ArrowLeft size={16} className="mr-2" /> {t("certificate.backButton")}
          </Button>
          <Button
            data-testid={CERTIFICATE.printButton}
            onClick={() => window.print()}
            className="bg-blue-800 hover:bg-blue-700 text-white"
          >
            <Printer size={16} className="mr-2" /> {t("certificate.printButton")}
          </Button>
        </div>

        {/* Certificate paper */}
        <div className={`relative rounded-2xl shadow-xl print:shadow-none overflow-hidden ${
          isMaster
            ? "bg-gradient-to-br from-amber-50 via-white to-blue-50 border border-amber-300 print:border-amber-500"
            : isMastery
            ? "bg-gradient-to-br from-amber-50/40 via-white to-white border border-amber-300/60 print:border-amber-400"
            : "bg-white border border-slate-200 print:border-slate-400"
        }`}>
          {/* Ornate borders */}
          <div className={`absolute inset-4 border-2 rounded-xl pointer-events-none ${
            isMaster ? "border-amber-600/70" : isMastery ? "border-amber-500/70" : "border-blue-900/70"
          }`} />
          <div className={`absolute inset-6 border rounded-lg pointer-events-none ${
            isMaster ? "border-blue-900/40" : isMastery ? "border-blue-900/50" : "border-amber-500/50"
          }`} />
          {/* Corner ornaments */}
          <div className={`absolute top-8 left-8 w-6 h-6 border-t-2 border-l-2 ${isMaster ? "border-amber-700" : isMastery ? "border-amber-600" : "border-amber-500"}`} />
          <div className={`absolute top-8 right-8 w-6 h-6 border-t-2 border-r-2 ${isMaster ? "border-amber-700" : isMastery ? "border-amber-600" : "border-amber-500"}`} />
          <div className={`absolute bottom-8 left-8 w-6 h-6 border-b-2 border-l-2 ${isMaster ? "border-amber-700" : isMastery ? "border-amber-600" : "border-amber-500"}`} />
          <div className={`absolute bottom-8 right-8 w-6 h-6 border-b-2 border-r-2 ${isMaster ? "border-amber-700" : isMastery ? "border-amber-600" : "border-amber-500"}`} />

          <div className="relative px-10 py-16 sm:px-20 sm:py-20 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_wealth-academy-46/artifacts/x15oz6g3_Peak%20Market%20Society%20Logo.png"
                alt="Peak Market Society"
                className="w-12 h-12 rounded-full object-contain bg-white ring-1 ring-blue-900/20"
              />
              <div className="text-left">
                <div className="font-display font-black text-slate-900 tracking-tight text-xl leading-none">
                  Peak Market Society
                </div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-amber-600 font-semibold mt-0.5">
                  {t("nav.tagline")}
                </div>
              </div>
            </div>

            <div className={`mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.32em] font-semibold ${
              isMaster ? "text-amber-700" : isMastery ? "text-amber-700" : "text-amber-600"
            }`}>
              {isMaster ? <Trophy size={14} /> : <Award size={14} />}
              {isMaster
                ? t("certificate.master.diplomaHeading")
                : isMastery
                ? t("certificate.masteryHeading", "Certificate of Mastery")
                : t("certificate.pageTitle")}
            </div>

            <p className="mt-10 text-slate-600 text-sm sm:text-base">
              {t("certificate.issuedTo")}
            </p>
            <h1 className="mt-4 font-display font-black text-slate-900 text-4xl sm:text-6xl tracking-tighter leading-none">
              {cert.user_name}
            </h1>

            <p className="mt-10 text-slate-600 text-sm sm:text-base">
              {isMaster ? t("certificate.master.forCompletingAll") : t("certificate.forCompleting")}
            </p>
            <h2 className={`mt-3 font-display font-bold text-2xl sm:text-3xl tracking-tight ${
              isMaster ? "text-amber-800" : isMastery ? "text-amber-800" : "text-blue-900"
            }`}>
              {isMaster ? t("certificate.master.programName") : cert.course_title}
            </h2>
            {cert.course_level && (
              <div className="mt-2 text-xs uppercase tracking-[0.24em] text-slate-500 font-semibold">
                {cert.course_level}{isMastery ? " · Mastery" : ""}
              </div>
            )}

            {isMaster && cert.courses_included?.length > 0 && (
              <div className="mt-10 flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
                {cert.courses_included.map((c) => (
                  <div
                    key={c.slug}
                    className="px-4 py-3 border border-amber-500/40 rounded-lg bg-white/70 text-left min-w-[200px]"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold flex items-center gap-1">
                      <CheckCircle2 size={10} /> {t("certificate.master.pillarLabel")}
                    </div>
                    <div className="text-sm font-semibold text-slate-800 mt-0.5">
                      {c.course_title}
                    </div>
                    <div className="text-[11px] font-mono text-slate-500 mt-1">
                      {c.code}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isMaster && cert.chapter_scores?.length > 0 && (
              <div className="mt-10 flex flex-wrap justify-center gap-4">
                {cert.chapter_scores.map((c) => (
                  <div
                    key={c.chapter_number}
                    className="px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-left min-w-[180px]"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold">
                      Ch. {c.chapter_number}
                    </div>
                    <div className="text-sm font-semibold text-slate-800 mt-0.5">
                      {c.chapter_title}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {c.best_score}%
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-8 text-left">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold">
                  {t("certificate.onDate", { date: issuedLabel })}
                </div>
                <div className="mt-2 font-serif-quote text-slate-800 text-lg italic">
                  {issuedLabel}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold">
                  {t("certificate.codeLabel")}
                </div>
                <div className="mt-2 font-mono text-slate-900 tracking-widest">
                  {cert.code}
                </div>
              </div>
            </div>

            <div className="mt-12 flex items-center justify-center gap-2 text-xs text-slate-500">
              <ShieldCheck size={14} className="text-blue-800" />
              <span>
                {t("certificate.verifyHint")}{" "}
                <span className="font-mono text-slate-700">{verifyUrl}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
